'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import type { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

type AutoplayPlugin = ReturnType<typeof Autoplay>;

export interface UseHeroBannerSlideOptions {
  slideCount: number;
  autoPlay?: boolean;
  interval?: number;
}

export interface UseHeroBannerSlideReturn {
  emblaRef: (instance: HTMLElement | null) => void;
  emblaApi: EmblaCarouselType | null;
  selectedIndex: number;
  scrollSnaps: number[];
  scrollTo: (index: number) => void;
  scrollNext: () => void;
  scrollPrev: () => void;
  canScrollNext: boolean;
  canScrollPrev: boolean;
  autoplayPlugin: AutoplayPlugin | null;
}

export const useHeroBannerSlide = ({
  slideCount,
  autoPlay = true,
  interval = 5000,
}: UseHeroBannerSlideOptions): UseHeroBannerSlideReturn => {
  const isActive = useMemo(() => slideCount > 1, [slideCount]);

  const autoplayPlugin = useMemo<AutoplayPlugin | null>(() => {
    if (!autoPlay || !isActive) return null;
    return Autoplay({
      delay: interval,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    });
  }, [autoPlay, interval, isActive]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: isActive,
      skipSnaps: false,
      active: isActive,
    },
    autoplayPlugin ? [autoplayPlugin] : [],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollStates = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const handleSelect = useCallback(
    (api: EmblaCarouselType) => {
      setSelectedIndex(api.selectedScrollSnap());
      updateScrollStates(api);
    },
    [updateScrollStates],
  );

  const handleReInit = useCallback(
    (api: EmblaCarouselType) => {
      setScrollSnaps(api.scrollSnapList());
      setSelectedIndex(api.selectedScrollSnap());
      updateScrollStates(api);
    },
    [updateScrollStates],
  );

  useEffect(() => {
    if (!emblaApi || !isActive) return;

    setScrollSnaps(emblaApi.scrollSnapList());
    handleSelect(emblaApi);

    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleReInit);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleReInit);
    };
  }, [emblaApi, handleSelect, handleReInit, isActive]);

  useEffect(() => {
    if (!emblaApi || !isActive) return;
    emblaApi.reInit();
  }, [emblaApi, isActive]);

  useEffect(() => {
    if (!autoplayPlugin || !emblaApi || !isActive) return;

    autoplayPlugin.play();
    return () => {
      autoplayPlugin.stop();
    };
  }, [autoplayPlugin, emblaApi, isActive]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi || !isActive) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, isActive],
  );

  const scrollNext = useCallback(() => {
    if (!emblaApi || !isActive) return;
    emblaApi.scrollNext();
  }, [emblaApi, isActive]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi || !isActive) return;
    emblaApi.scrollPrev();
  }, [emblaApi, isActive]);

  return {
    emblaRef,
    emblaApi: emblaApi ?? null,
    selectedIndex,
    scrollSnaps,
    scrollTo,
    scrollNext,
    scrollPrev,
    canScrollNext,
    canScrollPrev,
    autoplayPlugin,
  };
};
